import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaFilePdf, FaEye, FaSpinner, FaFileInvoiceDollar, FaFileAlt } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import Modal from '../components/Modal';
import { clientInvoices } from '../api';
import { pdf } from '@react-pdf/renderer';
import ClientInvoicePDF from '../components/ClientInvoicePDF';
import { getCompanyDetails } from '../config/CompanyDetails';

const STATUS_META = {
  proforma: { label: 'Proforma', color: '#b45309', bg: '#fffbeb' },
  pending: { label: 'Pending', color: '#d97706', bg: '#fffbeb' },
  issued: { label: 'Issued', color: '#2563eb', bg: '#eff6ff' },
  sent: { label: 'Sent', color: '#2563eb', bg: '#eff6ff' },
  paid: { label: 'Paid', color: '#16a34a', bg: '#f0fdf4' },
  partially_paid: { label: 'Partially Paid', color: '#16a34a', bg: '#f0fdf4' },
  overdue: { label: 'Overdue', color: '#dc2626', bg: '#fef2f2' },
  credited: { label: 'Credited', color: '#6b7280', bg: '#f3f4f6' },
  // Voided = the invoice was cancelled before any payment was received. Never label this
  // "Credited": nothing came in, so nothing is owed back.
  void: { label: 'Voided', color: '#6b7280', bg: '#f3f4f6' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' }
};

const fmtMoney = (n) => 'R ' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function ClientInvoices({ onLogout }) {
  const [user, setUser] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await clientInvoices.getAll();
      if (res.success) setInvoices(res.invoices || []);
      else toast.error(res.error || 'Failed to load invoices');
    } catch (err) {
      console.error('Error fetching invoices:', err);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (inv) => {
    try {
      const res = await clientInvoices.getOne(inv.id);
      if (res.success) setViewing(res.invoice || inv);
      else setViewing(inv);
    } catch {
      setViewing(inv);
    }
  };

  // ── Client-safe PDF: full detail from the client endpoint (no cost price) ──
  const generatePDF = async (inv) => {
    setPdfLoading(inv.id);
    try {
      let detail = { invoice: inv, items: inv.items || [], job: inv.job || null, quote: null, payments: [] };
      if (!inv.items) {
        const res = await clientInvoices.getOne(inv.id);
        if (res.success) detail = res.invoice;
      }
      const company = await getCompanyDetails();
      const blob = await pdf(
        <ClientInvoicePDF
          invoice={detail.invoice}
          job={detail.job}
          quote={detail.quote}
          payments={detail.payments}
          company={company}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(detail.invoice?.proforma_number || detail.invoice?.invoice_number || 'Document')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('📄 PDF downloaded');
    } catch (err) {
      console.error('PDF error:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setPdfLoading(null);
    }
  };

  const isProforma = (inv) => inv.status === 'proforma';
  const docNumber = (inv) => isProforma(inv) ? (inv.proforma_number || inv.invoice_number) : inv.invoice_number;

  return (
    <ClientLayout activeTab="invoices" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>📄 Invoices & Proformas</h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>View and download your documents</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading your documents...</p>
          </div>
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-white rounded-xl border p-16 text-center" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-5xl mb-4">📭</div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#0A1929' }}>No documents yet</h3>
          <p className="text-sm text-gray-500">Proformas and invoices will appear here once your jobs are invoiced.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E8ECEF' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-gray-50" style={{ borderColor: '#E8ECEF' }}>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Document</th>
                  <th className="px-4 py-3">Job</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const meta = STATUS_META[inv.status] || { label: inv.status || '—', color: '#64748b', bg: '#f1f5f9' };
                  return (
                    <tr key={inv.id} className="border-b hover:bg-gray-50 transition-colors" style={{ borderColor: '#F1F5F9' }}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {isProforma(inv) ? <FaFileAlt style={{ color: '#b45309' }} /> : <FaFileInvoiceDollar style={{ color: '#00A86B' }} />}
                          <button onClick={() => openDetail(inv)} className="font-semibold hover:underline text-left" style={{ color: '#0A1929' }}>
                            {docNumber(inv)}
                          </button>
                        </div>
                        <span className="text-xs text-gray-400">{isProforma(inv) ? 'Proforma' : 'Invoice'}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {inv.job?.job_number || '—'}
                        {Array.isArray(inv.pods) && inv.pods.length > 0 ? (
                          <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ color: '#047857', backgroundColor: '#f0fdf4' }}>
                            POD
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fmtDate(inv.invoice_date)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fmtDate(inv.due_date)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-right" style={{ color: '#0A1929' }}>{fmtMoney(inv.total_amount)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ color: meta.color, backgroundColor: meta.bg }}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {Array.isArray(inv.pods) && inv.pods.length > 0 ? (
                            <button
                              onClick={() => {
                                const withDoc = inv.pods.find((p) => p.image_url);
                                if (withDoc && withDoc.image_url) window.open(withDoc.image_url, '_blank', 'noopener');
                                else window.open('/client-pods', '_blank', 'noopener');
                              }}
                              title={`Proof of delivery: ${inv.pods.map((p) => p.pod_number || p.load_number || 'POD').join(', ')}`}
                              className="w-8 h-8 rounded-lg hover:bg-purple-50 text-gray-400 hover:text-purple-600 flex items-center justify-center"
                            >
                              <FaFileAlt size={14} />
                            </button>
                          ) : null}
                          <button onClick={() => openDetail(inv)} title="View" className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 flex items-center justify-center">
                            <FaEye size={14} />
                          </button>
                          <button onClick={() => generatePDF(inv)} title="Download PDF" disabled={pdfLoading === inv.id} className="w-8 h-8 rounded-lg hover:bg-green-50 text-gray-400 hover:text-green-600 flex items-center justify-center disabled:opacity-50">
                            {pdfLoading === inv.id ? <FaSpinner className="animate-spin" size={14} /> : <FaFilePdf size={14} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Invoice / Proforma Detail Modal ── */}
      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} title={viewing ? `Document ${docNumber(viewing)}` : 'Document'}>
        {viewing && (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {(() => { const meta = STATUS_META[viewing.status] || { label: viewing.status || '—', color: '#64748b', bg: '#f1f5f9' }; return (
                <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</span>
              ); })()}
              <span className="text-sm text-gray-500">{isProforma(viewing) ? 'Proforma' : 'Tax Invoice'}</span>
              <span className="text-sm text-gray-500">• Job {viewing.job?.job_number || '—'}</span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase mb-1">Job</p>
                <p className="text-gray-700">{viewing.job?.commodity || '—'}</p>
                <p className="text-xs text-gray-500 mt-1">{viewing.job?.origin || ''}{viewing.job?.origin && viewing.job?.destination ? ' → ' : ''}{viewing.job?.destination || ''}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase mb-1">Dates</p>
                <p className="text-gray-700">Issued: {fmtDate(viewing.invoice_date)}</p>
                <p className="text-xs text-gray-500 mt-1">Due: {fmtDate(viewing.due_date)}</p>
                {viewing.payment_terms ? <p className="text-xs text-gray-500 mt-1">{viewing.payment_terms}</p> : null}
              </div>
            </div>

            {(viewing.items && viewing.items.length > 0) && (
              <div className="mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-gray-500 uppercase">
                      <th className="py-2 pr-3">Description</th>
                      <th className="py-2 pr-3 text-right">Qty</th>
                      <th className="py-2 pr-3 text-right">Unit</th>
                      <th className="py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewing.items.map((it, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-2 pr-3 text-gray-700">{it.description}</td>
                        <td className="py-2 pr-3 text-right text-gray-600">{it.quantity_mt || it.quantity || ''}</td>
                        <td className="py-2 pr-3 text-right text-gray-600">{fmtMoney(it.rate_per_mt || it.unit_price)}</td>
                        <td className="py-2 text-right font-medium text-gray-800">{fmtMoney(it.amount || it.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="border-t pt-4 flex flex-col items-end gap-1 text-sm" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex justify-between w-64 text-gray-600">
                <span>Subtotal</span>
                <span>{fmtMoney(viewing.subtotal)}</span>
              </div>
              <div className="flex justify-between w-64 text-gray-600">
                <span>VAT ({viewing.vat_amount > 0 ? (viewing.vat_rate || 15) : 0}%)</span>
                <span>{fmtMoney(viewing.vat_amount)}</span>
              </div>
              <div className="flex justify-between w-64 font-bold text-base" style={{ color: '#0A1929' }}>
                <span>Total</span>
                <span>{fmtMoney(viewing.total_amount)}</span>
              </div>
              {Number(viewing.paid_amount) > 0 && (
                <div className="flex justify-between w-64 text-green-600">
                  <span>Paid</span>
                  <span>-{fmtMoney(viewing.paid_amount)}</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => generatePDF(viewing)} disabled={pdfLoading === viewing.id}
                className="px-6 py-2 rounded-lg text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: '#00A86B' }}>
                {pdfLoading === viewing.id ? <FaSpinner className="animate-spin" /> : <FaFilePdf />} Download PDF
              </button>
            </div>
          </div>
        )}
      </Modal>
    </ClientLayout>
  );
}

export default ClientInvoices;
